/**
 * Pricing helpers for the item detail screen.
 *
 * Variant option prices are **adjustments** added to the item's base price —
 * the same convention used everywhere else in the stack:
 *   - rendered as "+X" in the UI,
 *   - stored as `priceAdjustment` on the cart/order line,
 *   - summed as `base + variant` by `lib/pricing.ts` and the backend.
 *
 * Kept as a pure function (no React) so the money math is unit-testable in
 * isolation without a DOM.
 */

export type VariantOptionLite = { id: string; price: number };
export type VariantGroupLite = { id: string; options: VariantOptionLite[] };

/**
 * The per-unit base price shown/charged for the item with the current variant
 * selection: item base price plus every selected variant option's adjustment.
 */
export function computeEffectiveBasePrice(
  basePrice: number,
  variantGroups: ReadonlyArray<VariantGroupLite>,
  variantSel: Record<string, string>,
): number {
  let variantAmount = 0;
  for (const g of variantGroups) {
    const selId = variantSel[g.id];
    if (!selId) continue;
    const opt = g.options.find((o) => o.id === selId);
    if (opt) variantAmount += opt.price;
  }
  return basePrice + variantAmount;
}
